// Shell - top navigation, demo banner, page wrapper
import { G, GL, GB, BLACK, MID, LIGHT, BORDER, BG, PAGE } from "../constants";

const STEPS_FULL = [
  { num: "01", label: "Setup" },
  { num: "02", label: "Search Intel" },
  { num: "03", label: "Audit" },
  { num: "04", label: "Feed Review" },
  { num: "05", label: "GMC Fix", optional: true },
  { num: "06", label: "Review" },
  { num: "07", label: "Done" },
];

const STEPS_LITE = [
  { num: "01", label: "Connect" },
  { num: "02", label: "Scan & Fix" },
  { num: "03", label: "Done" },
];

export function Shell({ step, setStep, children, demoMode, onExitDemo, appMode = "full", setAppMode }) {
  const isLite = appMode === "lite";
  const steps = isLite ? STEPS_LITE : STEPS_FULL;
  const accentColor = isLite ? "#4285f4" : G;
  const accentLight = isLite ? "#eff6ff" : GL;
  const accentBorder = isLite ? "#bfdbfe" : GB;

  return (
    <div style={{ fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif", fontSize: 13, color: BLACK, background: PAGE, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; }
        @keyframes pop { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      `}</style>

      <div style={{ height: 48, background: BG, borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "stretch", paddingLeft: 24, flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginRight: 24, paddingRight: 24, borderRight: `1px solid ${BORDER}` }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: accentColor, display: "inline-block" }} />
          <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: "0.2px", color: accentColor, fontFamily: "inherit" }}>SIFT</span>
          <span style={{ color: BORDER, margin: "0 4px" }}>/</span>
          <span style={{ fontSize: 11, color: LIGHT, fontWeight: 500 }}>{isLite ? "GMC Feed Health" : "Signal Intelligence"}</span>
        </div>
        {steps.map((s, i) => {
          const active = step === i;
          const done = step > i;
          return (
            <button key={s.num} data-testid={`step-nav-${s.num}`} onClick={() => done && setStep(i)}
              style={{ border: "none", background: "none", cursor: done ? "pointer" : "default", height: "100%", padding: "0 16px", display: "flex", alignItems: "center", gap: 5, borderBottom: active ? `2px solid ${accentColor}` : "2px solid transparent", fontSize: 11, fontWeight: active ? 700 : 500, color: active ? accentColor : done ? MID : LIGHT, fontFamily: "inherit", transition: "color 0.15s", whiteSpace: "nowrap", letterSpacing: "0.1px" }}>
              <span style={{ fontWeight: active ? 800 : 500, opacity: 0.5, fontSize: 10 }}>{s.num}</span> {s.label}
              {s.optional && <span style={{ fontSize: 9, fontWeight: 700, color: "#d97706", background: "#fef3c7", border: "1px solid #fde68a", padding: "1px 5px", borderRadius: 3, letterSpacing: "0.3px" }}>OPT</span>}
            </button>
          );
        })}
        {/* Mode switcher */}
        {setAppMode && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", paddingRight: 20, gap: 4 }}>
            <button data-testid="mode-lite-btn" onClick={() => { setAppMode("lite"); }}
              style={{ border: `1px solid ${isLite ? "#bfdbfe" : BORDER}`, background: isLite ? "#eff6ff" : "transparent", borderRadius: "5px 0 0 5px", padding: "4px 10px", fontSize: 10, fontWeight: 700, color: isLite ? "#4285f4" : LIGHT, cursor: "pointer", fontFamily: "inherit" }}>
              Lite
            </button>
            <button data-testid="mode-full-btn" onClick={() => { setAppMode("full"); }}
              style={{ border: `1px solid ${!isLite ? GB : BORDER}`, background: !isLite ? GL : "transparent", borderRadius: "0 5px 5px 0", padding: "4px 10px", fontSize: 10, fontWeight: 700, color: !isLite ? G : LIGHT, cursor: "pointer", fontFamily: "inherit" }}>
              Full
            </button>
          </div>
        )}
      </div>

      {demoMode && (
        <div data-testid="demo-mode-banner" style={{ background: isLite ? "linear-gradient(90deg, #eff6ff 0%, #dbeafe 50%, #eff6ff 100%)" : "linear-gradient(90deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)", backgroundSize: "200% 100%", animation: "shimmer 4s linear infinite", borderBottom: `1px solid ${isLite ? "#bfdbfe" : "#bae6fd"}`, padding: "8px 24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: isLite ? "#1d4ed8" : "#0369a1", background: isLite ? "#dbeafe" : "#e0f2fe", border: `1px solid ${isLite ? "#93c5fd" : "#7dd3fc"}`, borderRadius: 4, padding: "2px 8px", letterSpacing: "0.5px" }}>DEMO</span>
            <span style={{ fontSize: 12, color: isLite ? "#1e3a8a" : "#0c4a6e", fontWeight: 500 }}>
              {isLite ? "SIFT Lite — GMC feed health fixer (3 demo SKUs)" : "SIFT App — full signal intelligence (3 SKUs, 11 sources)"}
            </span>
          </div>
          <button data-testid="exit-demo-btn" onClick={onExitDemo} style={{ background: "none", border: `1px solid ${isLite ? "#93c5fd" : "#7dd3fc"}`, borderRadius: 5, padding: "4px 12px", fontSize: 11, fontWeight: 600, color: isLite ? "#1d4ed8" : "#0369a1", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>Exit demo &rarr; Connect real store</button>
        </div>
      )}

      <div style={{ flex: 1, padding: "28px 32px 64px", maxWidth: 1020, width: "100%", margin: "0 auto", animation: "pop 0.25s ease" }}>
        <div style={{ background: BG, borderRadius: 12, padding: "32px 36px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function PageHead({ num, title, sub, onNext, nextLabel = "Continue \u2192", onBack, disabled = false }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: LIGHT, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 6 }}>STEP {num}</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: BLACK, margin: "0 0 6px", letterSpacing: "-0.2px" }}>{title}</h1>
          <p style={{ fontSize: 13, color: MID, margin: 0, fontWeight: 400 }}>{sub}</p>
        </div>
        {onNext && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0, paddingTop: 4 }}>
            {onBack && (
              <button onClick={onBack} style={{ border: `1px solid ${BORDER}`, background: BG, borderRadius: 6, padding: "7px 16px", fontSize: 12, color: MID, cursor: "pointer", fontFamily: "inherit" }}>&larr; Back</button>
            )}
            <button onClick={onNext} disabled={disabled}
              style={{ background: disabled ? "#e5e7eb" : G, color: disabled ? LIGHT : "#fff", border: "none", borderRadius: 6, padding: "7px 18px", fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {nextLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Footer({ onBack, onNext, nextLabel = "Continue \u2192", disabled = false }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
      {onBack && (
        <button onClick={onBack} style={{ border: `1px solid ${BORDER}`, background: BG, borderRadius: 6, padding: "8px 18px", fontSize: 12, color: MID, cursor: "pointer", fontFamily: "inherit" }}>&larr; Back</button>
      )}
      {onNext && (
        <button onClick={onNext} disabled={disabled}
          style={{ background: disabled ? "#e5e7eb" : G, color: disabled ? LIGHT : "#fff", border: "none", borderRadius: 6, padding: "8px 22px", fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
          {nextLabel}
        </button>
      )}
    </div>
  );
}
